<template>
  <div v-if="isOpen" :class="embedded ? 'embedded-wrap' : 'overlay'" @click.self="!embedded && handleClose()">
    <section class="modal" role="dialog" aria-modal="true" aria-label="Create submission" @click.stop>
      <header class="modal-head">
        <div class="header-center">
          <h1 class="title">Let's create your submission!</h1>
          <div class="stepper-wrap">
            <el-steps :active="step" finish-status="success" align-center class="stepper" :space="120">
              <el-step v-for="(label, idx) in stepTitles" :key="label" :title="label" />
            </el-steps>
          </div>
        </div>
        <el-button circle type="primary" class="close-btn" @click="handleClose" color="#003831">
          <el-icon><Close /></el-icon>
        </el-button>
      </header>

      <el-card class="card" shadow="never">
        <template v-if="step === 0">
          <h2 class="h2">Submission Type</h2>
          <el-card shadow="never" class="section-card">
            <el-radio-group v-model="form.submissionType" class="opt-grid">
              <el-radio-button v-for="opt in submissionOptions" :key="opt" :label="opt" class="opt-card" plain>
                <div class="opt-title">{{ opt }}</div>
              </el-radio-button>
            </el-radio-group>
          </el-card>
        </template>

        <template v-else-if="step === 1">
          <h2 class="h2">Submission Details</h2>
          <el-card class="section-card">
            <el-form label-position="left" label-width="200px" class="form-grid">
              <el-form-item label="Submission Name *">
                <el-input v-model="form.submissionName" placeholder="Enter submission name" clearable />
              </el-form-item>
              <el-form-item label="Sponsor *">
                <el-input v-model="form.sponsor" placeholder="Enter sponsor" clearable />
              </el-form-item>
              <el-form-item label="Sponsor Protocol Number *">
                <el-input v-model="form.protocolNumber" placeholder="If not applicable, enter 'NA'" clearable />
              </el-form-item>
              <el-form-item label="Protocol Title *">
                <el-input v-model="form.protocolTitle" type="textarea" :rows="2" placeholder="Enter protocol title" />
              </el-form-item>
            </el-form>
          </el-card>
        </template>

        <template v-else-if="step === 2">
          <h1 class="h2">Submit PI</h1>
          <h4 align="center">Are you also submitting a PI or site with this study?</h4>
          <el-card shadow="never" class="section-card">
            <el-radio-group v-model="form.submitPI" class="pill-group center-group" size="large">
              <el-radio-button v-for="choice in ['Yes','No']" :key="choice" :label="choice">
                {{ choice }}
              </el-radio-button>
            </el-radio-group>
          </el-card>
        </template>

        <template v-else-if="step === 3">
          <h2 class="h2">Disclosures</h2>
          <h4 align="center">Tell us a bit more about your submission</h4>
          <el-alert type="info" :closable="false" show-icon class="mb-3">
            Providing this information now will allow us to tell you what forms and documents are required in your submission as accurately as possible.
          </el-alert>
          <el-card shadow="never" class="info-card">
            <h4>Recruitment Bonuses</h4>
            <p>
              BGIR does not allow recruitment bonuses (<em>extra payments tied to the rate or timing of recruitment or enrollment</em>). BGIR does allow payment for time spent for screening for recruitment purposes on a fair market value basis to be built into the study budget. BGIR also allows paid patient referral programs. If you have a program you would like specific consideration/review for that fits within these criteria, please submit a standalone Administrative Letter for consideration.
            </p>
          </el-card>
          <h3 class="h3">Financial Interest Disclosure</h3>
          <p>
            Does the Principal Investigator (PI), the PI's immediate family, or any other research personnel or their immediate families, have any of the following financial interests in any entity that is sponsoring the research, or an entity that is manufacturing the product or service being tested, not reported to this IRB in previous submissions for this protocol?
          </p>
          <ul class="fi-list">
            <li><strong>Any remuneration</strong> from the entity in the previous twelve months that exceeds $5,000, when aggregated for the individual and their immediate family</li>
            <li><strong>Any equity interest</strong> in the entity</li>
            <li><strong>Any intellectual property rights and interests</strong></li>
            <li><strong>Any governance or executive relationship</strong> with the entity</li>
          </ul>
          <el-radio-group v-model="form.hasFinancialDisclosure" class="pill-group center-group" size="large">
            <el-radio-button v-for="choice in ['Yes','No']" :key="choice" :label="choice">
              {{ choice }}
            </el-radio-button>
          </el-radio-group>
        </template>

        <template v-else-if="step === 4">
          <h2 class="h2">Supporting Documents</h2>
          <el-card shadow="never" class="section-card">
            <p class="center muted">Upload any files that help reviewers understand your submission.</p>
            <el-upload
              ref="uploader"
              drag
              multiple
              :auto-upload="false"
              :show-file-list="false"
              :on-change="onUploadChange"
              class="upload-block"
            >
              <el-icon class="dz-icon"><UploadFilled /></el-icon>
              <div class="dz-title">Drag & drop files here</div>
              <div class="dz-sub">or click to choose from your computer</div>
            </el-upload>

            <div v-if="files.length" class="file-list">
              <el-card
                v-for="(f, idx) in files"
                :key="f.id"
                class="file-chip"
                shadow="never"
                :body-style="{ padding: '10px 12px' }"
              >
                <div class="file-left">
                  <div class="file-ext">{{ f.ext }}</div>
                  <div class="file-meta">
                    <div class="file-name">{{ f.file.name }}</div>
                    <div class="file-size">{{ prettySize(f.file.size) }}</div>
                  </div>
                </div>
                <el-button type="danger" link size="small" class="chip-x" @click="removeFile(idx)">Remove</el-button>
              </el-card>
            </div>
          </el-card>
        </template>

        <template v-else>
          <h2 class="h2">Review & Submit</h2>
          <el-card shadow="never" class="section-card">
            <el-descriptions :column="1" border class="review-box">
              <el-descriptions-item label="Submission Type">{{ form.submissionType || '—' }}</el-descriptions-item>
              <el-descriptions-item label="Submission Name">{{ form.submissionName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="Protocol Title">{{ form.protocolTitle || '—' }}</el-descriptions-item>
              <el-descriptions-item label="Sponsor">{{ form.sponsor || '—' }}</el-descriptions-item>
              <el-descriptions-item label="Protocol #">{{ form.protocolNumber || '—' }}</el-descriptions-item>
              <el-descriptions-item label="Submit PI?">{{ form.submitPI || '—' }}</el-descriptions-item>
              <el-descriptions-item label="Financial Disclosure">{{ form.hasFinancialDisclosure || '—' }}</el-descriptions-item>
              <el-descriptions-item label="Attached Files">
                <div v-if="files.length" class="review-files">
                  <div v-for="f in files" :key="f.id" class="review-file">
                    <span class="file-name">{{ f.file.name }}</span>
                    <span class="file-size">{{ prettySize(f.file.size) }}</span>
                  </div>
                </div>
                <span v-else>—</span>
              </el-descriptions-item>
            </el-descriptions>

          </el-card>
        </template>

      </el-card>
      <div class="nav-footer">
        <el-button type="default" :disabled="step === 0" @click="prev">Back</el-button>
        <el-button
          v-if="step < totalSteps - 1"
          type="primary"
          :disabled="!isStepValid"
          @click="next"
        >
          Continue
        </el-button>
        <el-button
          v-else
          type="primary"
          :loading="submitting"
          @click="submit"
        >
          {{ submitting ? 'Submitting…' : 'Submit Case' }}
        </el-button>
      </div>
    </section>
  </div>

  <teleport to="body">
    <div v-if="notice" class="toast-wrap" :class="`toast-${notice.type}`" role="status" aria-live="polite">
      <div class="toast">
        <div class="toast-msg">{{ notice.text }}</div>
        <el-button
          class="toast-close"
          text
          @click="dismissNotice"
          aria-label="Dismiss"
        >
          ×
        </el-button>
      </div>
    </div>
  </teleport>

  <teleport to="body">
    <div
      v-if="resultDialog.visible"
      class="result-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Submission result"
    >
      <div class="result-panel" :class="resultDialog.type">
        <div class="result-message">{{ resultDialog.text }}</div>
        <el-button class="result-ok" type="primary" @click="dismissResult">OK</el-button>
      </div>
    </div>
  </teleport>
</template>

<script>
import { auth } from '@/firebase'
import { ref as sRef, uploadBytes } from 'firebase/storage'
import { storage } from '@/firebase'
import { finalizeCase } from '@/services/cases'
import { ElMessageBox } from 'element-plus'
export default {  
  name: 'MultiStepForm',
  emits: ['form-completed','close','update:modelValue', 'click'],
  props: {
    modelValue: { type: Boolean, default: undefined },
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      internalOpen: true,
      step: 0,
      totalSteps: 6,
      files: [],
      submitting: false,
      notice: null,
      noticeTimer: null,
      resultDialog: { visible: false, type: 'success', text: '' },
      submissionOptions: [
        'A New Study for Initial Review',
        'Clinical Use of Humanitarian Use Device (HUD)',
        'Exemption Request',
        'Single Patient Expanded Access Program/Compassionate or Emergency Use'
      ],
      optionDescriptions: {
        'A New Study for Initial Review': 'Start a new study submission for ethics review.',
        'Clinical Use of Humanitarian Use Device (HUD)': 'Submit a request for clinical HUD usage.',
        'Exemption Request': 'Request exemption for non-research projects.',
        'Single Patient Expanded Access Program/Compassionate or Emergency Use':
          'Submit an emergency access or compassionate use request.'
      },
      form: this.blankForm()
    };
  },
  computed: {
    stepTitles() {
      return ['Step 1','Step 2','Step 3','Step 4','Step 5','Step 6']
    },
    isOpen() {
      return typeof this.modelValue === 'boolean'
        ? this.modelValue && this.internalOpen
        : this.internalOpen;
    },
    isStepValid() {
      switch (this.step) {
        case 0:
          return !!this.form.submissionType;
        case 1:
          return (
            this.hasText(this.form.submissionName) &&
            this.hasText(this.form.protocolTitle) &&
            this.hasText(this.form.sponsor) &&
            this.hasText(this.form.protocolNumber)
          );
        case 2:
          return !!this.form.submitPI;
        case 3:
          return !!this.form.hasFinancialDisclosure;
        case 4:
          return true;
        default:
          return true;
      }
    }
  },
  watch: {
    modelValue(v) {
      if (v) {
        this.internalOpen = true;
      }
    }
  },
  methods: {
    hasText(value) {
      return typeof value === 'string' && value.trim().length > 0
    },
    normalizeText(value) {
      return typeof value === 'string' ? value.trim() : ''
    },
    blankForm() {
      return {
        submissionType: '',
        submissionName: '',
        protocolTitle: '',
        sponsor: '',
        protocolNumber: '',
        submitPI: '',
        hasFinancialDisclosure: ''
      };
    },
    resetAll() {
      this.step = 0;
      this.form = this.blankForm();
      this.files = [];
      if (this.$refs.uploader?.clearFiles) this.$refs.uploader.clearFiles();
    },
    next() { if (this.step < this.totalSteps - 1 && this.isStepValid) this.step++; },
    prev() { if (this.step > 0) this.step--; },
    handleClose() {
      this.resetAll();
      this.internalOpen = false;
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    showNotice(type, text) {
      this.notice = { type, text };
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => { this.notice = null; }, 6000);
    },
    dismissNotice() {
      clearTimeout(this.noticeTimer);
      this.notice = null;
    },
    showResult(type, text) {
      this.resultDialog.type = type
      this.resultDialog.text = text
      this.resultDialog.visible = true
    },
    dismissResult() {
      this.resultDialog.visible = false
    },
    async submit() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Please sign in to submit');
        const tempId = `${Date.now()}_${Math.random().toString(36).slice(2,8)}`
        const prefix = `uploads/${user.uid}/staging/${tempId}/`
        const uploads = (this.files || []).map(async (f) => {
          const raw = f.file
          if (!raw) return
          const safeName = raw.name.replace(/[^\w.\-]+/g, '_')
          const path = `${prefix}${safeName}`
          const fileRef = sRef(storage, path)
          await uploadBytes(fileRef, raw, { contentType: raw.type || 'application/octet-stream' })
        })
        await Promise.all(uploads)

        await new Promise((resolve) => setTimeout(resolve, 800))

        const submission = {
          submissionType: this.form.submissionType || '',
          submissionName: this.normalizeText(this.form.submissionName),
          protocolTitle: this.normalizeText(this.form.protocolTitle),
          sponsor: this.normalizeText(this.form.sponsor),
          protocolNumber: this.normalizeText(this.form.protocolNumber),
          submitPI: this.form.submitPI || '',
          hasFinancialDisclosure: this.form.hasFinancialDisclosure || ''
        }

        const tries = 3
        let caseId = null, lastErr = null
        for (let i = 0; i < tries; i++) {
          try {
            caseId = await finalizeCase(tempId, submission)
            if (caseId) break
          } catch (e) {
            lastErr = e
            const delay = 600 * Math.pow(2, i)
            await new Promise(r => setTimeout(r, delay))
          }
        }
        if (!caseId) {
          const msg = lastErr?.message || 'Finalization failed'
          throw new Error(msg)
        }

        this.handleClose()
        await this.$nextTick()
        await ElMessageBox.alert(
          'Your case has been submitted. Please look out for our mail with further details.',
          'Success',
          { type: 'success', confirmButtonText: 'OK' }
        )
        this.$emit('form-completed', { caseId })
      } catch (err) {
        const msg = err?.message || err?.code || 'Failed to submit case'
        this.handleClose()
        await this.$nextTick()
        await ElMessageBox.alert(
          `${msg}\n\nPlease try again.`,
          'Error',
          { type: 'error', confirmButtonText: 'OK' }
        )
      } finally {
        this.submitting = false;
      }
    },
    onUploadChange(uploadFile) {
      const raw = uploadFile?.raw || uploadFile?.file
      if (!raw) return
      this.addFiles([raw])
    },
    addFiles(fileList) {
      const list = Array.isArray(fileList) ? fileList : Array.from(fileList || [])
      const newItems = list.map(file => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2,7)}`,
        file,
        ext: (file.name.split('.').pop() || '').slice(0,4).toUpperCase()
      }));
      this.files = [...this.files, ...newItems].slice(0, 20);
    },
    removeFile(idx) {
      this.files.splice(idx, 1);
    },
    prettySize(bytes) {
      if (bytes == null || isNaN(bytes)) return '—'
      if (bytes < 1024) return `${bytes} B`
      const kb = bytes / 1024
      if (kb < 1024) return `${kb.toFixed(1)} KB`
      const mb = kb / 1024
      if (mb < 1024) return `${mb.toFixed(2)} MB`
      const gb = mb / 1024
      return `${gb.toFixed(2)} GB`
    }
  }
};
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.48);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 22px;
  box-sizing: border-box;
}

.embedded-wrap {
  display: grid;
  place-items: center;
  padding: 12px;
}

.modal {
  width: min(1000px, 96vw);
  height: 85vh;
  max-height: 85vh;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 28px 80px rgba(0,56,49,0.25);
  padding: 20px 22px 22px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-head {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  position: relative;
}

.title {
  margin: 0 0 6px;
  color: var(--ink);
  font-weight: 800;
  text-align: center;
}

.stepper-wrap { display:flex; justify-content:center; width: 100%; }
.stepper { margin-bottom: 8px; width: 100%; }

.close-btn {
  position: absolute;
  top: 0;
  right: 0;
}

.card { background: #f8fbfa; border: 1px solid var(--border-soft); flex: 1; overflow: auto;}
.section-card { background:#fff; border:1px solid var(--border-soft)}
.h2 { text-align:center; color:var(--ink); font-weight:800; font-size:22px; margin: 1px; padding-bottom: 10px;}
.h3 { font-weight:800; color:var(--ink); margin-top: 12px; }
.muted { color: var(--muted); }
.helper { color: var(--muted); display:block; margin-top:6px; }

.opt-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 14px;
}

.opt-card {
  width: 100%;
  border: 1px solid var(--border-soft);
  border-radius: 14px;
}

.opt-card :deep(.el-radio-button__inner) {
  width: 100%;
  min-height: 100px;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 14px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.opt-card :deep(.el-radio-button__inner:hover) {
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.opt-title { font-weight: 800; }

.form-grid { display:grid; gap:5px; }
.inline-form :deep(.el-form-item) { align-items: center; }
.inline-form :deep(.el-form-item__label) { padding-right: 12px; font-weight: 700; }
.inline-form :deep(.el-form-item__content) { flex: 1; }

.pill-group { display: flex; gap: 8px; flex-wrap: wrap; }
.center-group { justify-content: center; }

.info-card { background: #fdfefc; border: 1px solid var(--border-soft); margin-bottom: 12px; }
.info-card h4 { margin:0 0 6px; color: var(--ink); }

.fi-list { margin:10px 0 16px; padding-left:18px; list-style:disc; }

.upload-block { margin: 10px auto 0; max-width: 600px; }
.upload-block :deep(.el-upload-dragger) {
  border: 2px dashed rgba(0,56,49,0.25);
  background: #fff;
}
.dz-icon { font-size:22px; color: var(--primary-strong); }
.dz-title { font-weight: 800; color: var(--ink); margin-top: 6px; }
.dz-sub { color: var(--muted); font-size: .95rem; }

.file-list { display:grid; grid-template-columns:1fr; gap:10px; margin-top:14px; max-width: 720px; margin-left: auto; margin-right: auto; }
.file-chip { display:flex; align-items:center; justify-content:space-between; border:1px solid var(--border-soft); }
.file-left { display:flex; gap:10px; align-items:center; min-width:0; }
.file-ext { width:38px; height:38px; border-radius:8px; background:var(--surface-muted); color:var(--primary-strong); display:grid; place-items:center; font-weight:800; }
.file-meta { min-width:0; }
.file-name { color:var(--ink); font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.file-size { color:var(--muted); font-size:0.9rem; }

.review-box { margin: 12px 0; }
.review-files { display:flex; flex-direction:column; gap:6px; }
.review-file { display:flex; justify-content:space-between; gap:8px; color:var(--muted); font-size:0.95rem; }

.submit-center { display:flex; justify-content:center; align-items:center; margin-top:12px; }
.center { text-align:center; }

.nav-footer {
  display:flex;
  justify-content:space-between;
  gap: 10px;
  padding: 14px 0 4px;
  position: sticky;
  bottom: 0;
  background: #fff;
}

@media (max-width:720px) {
  .modal { width: 100%; padding: 16px; height: 90vh; max-height: 90vh; }
  .modal-head { flex-direction: column; align-items: flex-start; }
  .opt-grid { grid-template-columns:1fr; }
  .nav-footer { flex-direction: column; }
  .nav-footer .el-button { width: 100%; }
}

.toast-wrap {
  position:fixed;
  bottom:24px;
  right:24px;
  z-index:4000;
  display:flex;
  max-width:380px;
  width:calc(100vw - 48px);
  animation:fadeIn .25s ease;
  font-family:"Roboto","Segoe UI",sans-serif;
}
.toast {
  background:#fff;
  border:1px solid rgba(0,0,0,0.12);
  border-left:6px solid #0b6a59;
  border-radius:12px;
  padding:14px 16px 12px;
  box-shadow:0 12px 32px rgba(0,0,0,0.18);
  display:flex;
  gap:12px;
  align-items:flex-start;
  width:100%;
}
.toast-error .toast { border-left-color:#c62828; }
.toast-success .toast { border-left-color:#0b6a59; }
.toast-msg { flex:1; font-size:.95rem; color:#1f2937; line-height:1.35; font-weight:600; }
.toast-close.el-button {
  background:transparent;
  border:none;
  color:#003831;
  font-size:20px;
  line-height:1;
  cursor:pointer;
  font-weight:700;
  padding:2px 6px;
  border-radius:6px;
  min-height: auto;
}
.toast-close.el-button.is-text:hover { background:rgba(0,0,0,0.06); }

.result-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4500;
  font-family: "Roboto","Segoe UI",sans-serif;
}
.result-panel {
  width: min(420px, 90vw);
  background: #101317;
  color: #fff;
  border-radius: 18px;
  padding: 28px 26px 24px;
  box-shadow: 0 30px 70px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  gap: 22px;
  position: relative;
  border: 1px solid rgba(255,255,255,0.08);
}
.result-panel.success { border-left: 6px solid #0b6a59; }
.result-panel.error { border-left: 6px solid #c62828; }
.result-message {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
}
.result-ok.el-button {
  align-self: flex-end;
  padding: 10px 22px;
  font-size: 0.95rem;
  font-weight: 700;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  background: linear-gradient(90deg,#003831,#0b6a59);
  color: #fff;
  box-shadow: 0 10px 24px rgba(0,56,49,0.35);
  transition: background .18s ease, transform .18s ease;
}
.result-ok.el-button:hover { background:#145e55; transform: translateY(-2px); }
.result-ok.el-button:focus-visible {
  outline: 3px solid rgba(11,92,255,0.3);
  outline-offset: 2px;
}
@keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
</style>
